/**
 * SOFSEK — Monitored-subject attributes on the platform user record.
 *
 * A monitored subject IS a user: migration 20260812010000 folded `sfk_app_user`
 * into `csm_user_master`, leaving only the four attributes the platform record
 * has no home for on the 1:1 child `sfk_subject_profile`. Whether that person can
 * also SIGN IN is the platform's own concern — the "Can log in to the system"
 * control on the Users screen, which creates, bans or un-bans the GoTrue login.
 * There is no SOFSEK-specific "is an app user" flag and there must never be one.
 *
 * This manifest is what makes the Users screen show those attributes without
 * SOFSEK forking the inherited page (§16). The platform renders it: the fields
 * become grid columns and facets, the children become related-children cards and
 * an address editor, and saving the user saves the profile.
 *
 * Why a sister table and not DSE shadow-fields (§10): every field here needs
 * something jsonb cannot give — `external_reference` is UNIQUE, `home_org_node_id`
 * is a real FK that the `sfk_node_in_scope()` RLS predicate joins on, and the
 * addresses/accounts/groups are 1:N collections.
 *
 * Deliberately NOT surfaced:
 *   • `anonymized_token` — system-managed DSAR scrub token (§24), not user data.
 *   • `is_active` — duplicates csm_user_master.is_active (all 383 rows agree);
 *     the host's own Active control is the single source of truth.
 */
import { registerRecordExtension } from '../../lib/record_extensions';

registerRecordExtension({
  key: 'sfk_subject',
  page_code: 'CSM.USER.LIST',
  entity: 'csm_user_master',
  sister: { table: 'sfk_subject_profile', pk: 'user_id' },
  section_label_key: 'sfk.subject.section',
  section_label_default: 'Monitored Subject',
  fields: [
    {
      // The source-system identifier this person is known by (e.g. 'U1'). Assigned
      // once at creation — re-keying it would orphan every ingested access fact.
      key: 'external_reference',
      label_key: 'sfk.subject.reference',
      label_default: 'Reference',
      kind: 'text',
      grid: true,
      grid_width: 130,
      immutable_after_create: true,
    },
    {
      key: 'app_user_type_code',
      label_key: 'sfk.subject.type',
      label_default: 'Subject Type',
      kind: 'lookup',
      lookup: { table: 'sfk_app_user_type', tl: 'sfk_app_user_type_tl' },
      grid: true,
      grid_width: 140,
      facet: true,
    },
    {
      // The monitored org tree — distinct from the CSM company/division hierarchy.
      key: 'home_org_node_id',
      label_key: 'sfk.subject.branch',
      label_default: 'Branch',
      kind: 'fk',
      ref: { table: 'sfk_org_node', pk: 'org_node_id', label_column: 'name' },
      grid: true,
      grid_width: 170,
      facet: true,
    },
  ],
  children: [
    {
      kind: 'address',
      key: 'addresses',
      label_key: 'sfk.subject.addresses',
      label_default: 'Addresses',
      table: 'sfk_app_user_address',
      pk: 'app_user_address_id',
      fk: 'app_user_id',
      types: ['HOME', 'MAILING', 'WORK'],
    },
    {
      // Accounts are discovered by ingestion runs, never typed in — read-only here.
      kind: 'related',
      key: 'accounts',
      label_key: 'sfk.subject.accounts',
      label_default: 'Accounts',
      table: 'sfk_app_user_account',
      pk: 'app_user_account_id',
      fk: 'app_user_id',
      label_column: 'external_username',
    },
    {
      kind: 'related_via',
      key: 'groups',
      label_key: 'sfk.subject.groups',
      label_default: 'Groups',
      junction: 'sfk_app_user_group_member',
      junction_pk: 'member_id',
      fk: 'app_user_id',
      target_fk: 'group_id',
      target: { table: 'sfk_app_user_group', pk: 'group_id', tl: 'sfk_app_user_group_tl' },
    },
  ],
});
